"use client";

import { useEffect } from "react";
import { useTranslation, type TranslationKey } from "@/lib/i18n";

interface MetadataUpdaterProps {
  titleKey?: TranslationKey;
  descriptionKey?: TranslationKey;
  /** For dynamic content where translations aren't in the i18n system */
  title?: { de: string; en: string };
  description?: { de: string; en: string };
  /** If true, use title as-is without appending " | HAINTZ IT-SOLUTIONS" */
  isRootPage?: boolean;
}

export default function MetadataUpdater({
  titleKey,
  descriptionKey,
  title,
  description,
  isRootPage = false,
}: MetadataUpdaterProps) {
  const { locale, t } = useTranslation();

  useEffect(() => {
    document.documentElement.lang = locale;

    const resolvedTitle = titleKey ? t(titleKey) : title?.[locale];
    const resolvedDescription = descriptionKey
      ? t(descriptionKey)
      : description?.[locale];

    if (resolvedTitle) {
      document.title = isRootPage
        ? resolvedTitle
        : `${resolvedTitle} | HAINTZ IT-SOLUTIONS`;
    }

    if (resolvedDescription) {
      const descMeta = document.querySelector('meta[name="description"]');
      if (descMeta) descMeta.setAttribute("content", resolvedDescription);

      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute("content", resolvedDescription);
    }

    if (resolvedTitle) {
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute("content", resolvedTitle);
    }

    const ogLocale = document.querySelector('meta[property="og:locale"]');
    if (ogLocale) {
      ogLocale.setAttribute("content", locale === "en" ? "en_US" : "de_AT");
    }
  }, [locale, t, titleKey, descriptionKey, title, description, isRootPage]);

  return null;
}
